import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, BellRing, CircleHelp, HelpCircle, ListChecks, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import Pagination from '@/client/components/common/Pagination';
import StatsGrid, { type Stat } from '@/client/components/common/StatsGrid';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useDataScopeStore } from '@/client/stores/data-scope';
import { useAvailabilityQuery } from '@/client/api/listing-management/entity.query';
import { getApiErrorMessage } from '@/client/utils/errors';

const STATUS_OPTIONS: [string, string][] = [
  ['', 'All statuses'],
  ['attention', 'Needs attention'],
  ['asked', 'Asked, no answer'],
  ['due', 'Due'],
  ['confirmed', 'Confirmed'],
  ['new', 'New'],
];

const COUNT_OPTIONS: [string, string][] = [
  ['', 'Any count'],
  ['stated', 'Count stated'],
  ['unstated', 'No count'],
];

const STATUS_TONE: Record<string, string> = {
  asked: 'bg-amber-100 text-amber-700',
  due: 'bg-red-100 text-red-700',
  confirmed: 'bg-emerald-100 text-emerald-700',
  new: 'bg-blue-100 text-blue-700',
};

// Column header -> `sort` value the API understands.
const COLUMNS: { label: string; sort?: string }[] = [
  { label: 'Listing', sort: 'name' },
  { label: 'Owner' },
  { label: 'Units', sort: 'units' },
  { label: 'Status', sort: 'stale' },
  { label: 'Confirmed' },
  { label: 'Asked' },
  { label: 'Posted', sort: 'date_added' },
  { label: 'Expires', sort: 'date_expired' },
];

// The API sends naive UTC timestamps.
const fmtDate = (v: string | null): string => {
  if (!v) return '—';
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? v : `${v}Z`);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
};

const AvailabilityPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Listing Management', href: '#' }, { label: 'Availability' }]);

  const scope = useDataScopeStore((s) => s.scope);
  const [sort, setSort] = useState('stale');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const { searchTerm, debouncedSearchTerm, filters, page, limit, handleSearch, handleFilterChange, handlePageChange, handleLimitChange } =
    useListFilters({
      initialFilters: { status: '', count: '', uid: '' },
      namespace: 'avail',
    });

  const { data, isLoading, isFetching, isError, error } = useAvailabilityQuery({
    status: filters.status || undefined,
    count: filters.count || undefined,
    uid: Number(filters.uid) || undefined,
    search: debouncedSearchTerm || undefined,
    sort,
    order,
    page,
    per_page: limit,
    test: scope,
  });

  const toggleSort = (col: string) => {
    if (col === sort) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(col);
      setOrder('asc');
    }
    handlePageChange(1);
  };

  const summary = data?.summary;
  const days = data?.check_every_days ?? 14;
  const stats: Stat[] = [
    { key: '', label: 'Listings', value: summary?.total, icon: ListChecks },
    { key: 'attention', label: 'Needs attention', value: summary?.attention, hint: `Silent over ${days} days`, icon: BellRing, tone: 'bg-red-50 text-red-600' },
    { key: 'asked', label: 'Asked, no answer', value: summary?.asked, icon: HelpCircle, tone: 'bg-amber-50 text-amber-600' },
    { key: 'unstated', label: 'No count stated', value: summary?.unstated, icon: CircleHelp },
  ];
  // Tiles drive the filters: the first three the status, the last the count.
  const onStat = (key: string) => {
    if (key === 'unstated') handleFilterChange('count', filters.count === 'unstated' ? '' : 'unstated');
    else handleFilterChange('status', key === filters.status ? '' : key);
  };
  const activeKeys = [filters.status, filters.count === 'unstated' ? 'unstated' : ''].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-8xl space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Availability</h1>
          <p className="mt-1 text-sm text-gray-500">
            Live listings outside a store, and whether their sellers have said they are still available. Read-only.
          </p>
        </div>

        <StatsGrid stats={stats} isLoading={isLoading && !data} onStatClick={onStat} activeKeys={activeKeys} />
        {data?.truncated && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            Too many listings to load at once: the counts and rows below cover only part of them.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search by listing name…"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <select
            aria-label="Status"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {STATUS_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select
            aria-label="Count"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={filters.count}
            onChange={(e) => handleFilterChange('count', e.target.value)}
          >
            {COUNT_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <Input
            aria-label="Owner uid"
            className="w-32"
            inputMode="numeric"
            placeholder="Owner uid"
            value={filters.uid}
            onChange={(e) => handleFilterChange('uid', e.target.value.replace(/\D/g, ''))}
          />
          {isFetching && <span className="text-xs text-gray-400">Loading…</span>}
        </div>

        {isError ? (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-6 text-sm text-red-600">
            {getApiErrorMessage(error, 'Could not load listing availability.')}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {COLUMNS.map(({ label, sort: col }) => (
                    <th key={label} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      {col ? (
                        <button type="button" className="inline-flex items-center gap-1 uppercase" onClick={() => toggleSort(col)}>
                          {label}
                          {sort === col && (order === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                        </button>
                      ) : (
                        label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading && !data
                  ? Array.from({ length: limit }).map((_, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3" colSpan={COLUMNS.length}>
                          <Skeleton className="h-5 w-full" />
                        </td>
                      </tr>
                    ))
                  : (data?.results ?? []).map((r) => (
                      <tr key={r.eid} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <Link to={`/admin/listings/${r.eid}`} className="text-blue-600 hover:underline">
                            {r.name || `#${r.eid}`}
                          </Link>
                          {r.tester && (
                            <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">Test</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {r.owner_name || r.owner_username || (r.uid != null ? `#${r.uid}` : '—')}
                          {r.owner_username && r.owner_name && <div className="text-xs text-gray-400">@{r.owner_username}</div>}
                        </td>
                        <td className="px-4 py-3 text-sm tabular-nums text-gray-900">{r.units_available ?? '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_TONE[r.status] ?? 'bg-gray-100 text-gray-600'}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(r.date_confirmed)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(r.date_asked)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(r.date_added)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(r.date_expired)}</td>
                      </tr>
                    ))}
                {!isLoading && (data?.results?.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-4 py-10 text-center text-sm text-gray-500">
                      No listings match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <Pagination
            total={data?.total_entities ?? 0}
            page={page}
            itemsPerPage={limit}
            limit={limit}
            setPage={handlePageChange}
            setItemsPerPage={handleLimitChange}
            showButtons
            showSelect
            selectOptions={[10, 20, 30, 50, 100]}
          />
        </div>
      </div>
    </div>
  );
};

export default AvailabilityPage;
